/**
 * まちがい記録 → 苦手把握 → ふくしゅう出題(公文式の反復学習の土台)。
 *
 * **描画にも保存先にも依存しない純TS**(CLAUDE.md ルール1 と同じ鉄則)。
 * localStorage も Firestore も知らない。呼び出し側(`apps/web` の `quizProgress`)が
 * 「記録を渡す → 新しい記録を受け取って保存する」だけで使える形にしてある。
 *
 * 決めごと:
 *   1. 1問あたり `{せいかい数, まちがい数, 直近の連続せいかい, 直近日時}` の4つだけ持つ
 *      (問題は数千問に増える。`QUIZ_REVIEW.maxTracked` で件数の上限も掛ける)
 *   2. **にがて** = 「直近がまちがい」または「累計のまちがいが多く、まだ解き直せていない」
 *   3. ふくしゅうで解ければ苦手プールから抜ける(何度もまちがえた問題だけ2回連続で必要)
 *   4. skills タグ・教科の集計は**この層の純関数**で出す(将来のレポート・AIチューター用)
 */

import { QUIZ_REVIEW } from './config.js';
import type { QuizQuestion, QuizSubject } from './types.js';

/**
 * 問題1問ぶんの解答履歴。
 *
 * `streak` は**直近の連続せいかい数**。記録は1回でも解答しないと生まれないので、
 * `streak === 0` はそのまま「直近でまちがえた」を意味する(直近結果を別に持たずに済む)。
 */
export interface QuizQuestionStat {
  readonly questionId: string;
  /** 累計のせいかい数。 */
  readonly correct: number;
  /** 累計のまちがい数。 */
  readonly wrong: number;
  /** 直近の連続せいかい数(0 = 直近でまちがえた)。 */
  readonly streak: number;
  /** 直近にといた時刻(epoch ms)。 */
  readonly lastAt: number;
}

/** 1問こたえた事実。 */
export interface QuizAnsweredQuestion {
  readonly questionId: string;
  readonly correct: boolean;
}

/** 直近の解答が正解だったか。 */
export function wasLastCorrect(stat: QuizQuestionStat): boolean {
  return stat.streak > 0;
}

/** 何度もまちがえている問題か(`QUIZ_REVIEW.repeatedWrong` 回以上)。 */
export function isRepeatedMistake(stat: QuizQuestionStat): boolean {
  return stat.wrong >= QUIZ_REVIEW.repeatedWrong;
}

/**
 * 苦手プールから抜けるのに要る連続せいかい数。
 *
 * ふつうは1回(= ふくしゅうで解ければその場で消える)。
 * 何度もまちがえている問題だけ2回連続にして、まぐれ当たりで消えないようにする。
 */
export function requiredStreakOf(stat: QuizQuestionStat): number {
  return isRepeatedMistake(stat) ? QUIZ_REVIEW.repeatedClearStreak : QUIZ_REVIEW.clearStreak;
}

/**
 * にがてな問題か。
 *
 * 一度もまちがえていない問題は対象外(`wrong === 0`)。まちがえた問題は
 * `requiredStreakOf` 回ぶん続けて正解するまで苦手プールに残る。
 */
export function isWeakQuestion(stat: QuizQuestionStat): boolean {
  if (stat.wrong <= 0) return false;
  return stat.streak < requiredStreakOf(stat);
}

/**
 * 1問ぶんの解答を記録した**新しい**履歴を返す(前の履歴は変更しない)。
 *
 * @param previous それまでの履歴(はじめてなら undefined)
 * @param answer   こたえた事実
 * @param at       といた時刻(epoch ms)
 */
export function recordQuizAnswer(
  previous: QuizQuestionStat | undefined,
  answer: QuizAnsweredQuestion,
  at: number,
): QuizQuestionStat {
  const base = previous ?? { questionId: answer.questionId, correct: 0, wrong: 0, streak: 0 };
  return {
    questionId: answer.questionId,
    correct: base.correct + (answer.correct ? 1 : 0),
    wrong: base.wrong + (answer.correct ? 0 : 1),
    // まちがえたら連続はリセット。正解したら1つ伸ばす。
    streak: answer.correct ? base.streak + 1 : 0,
    lastAt: at,
  };
}

/**
 * 1セッションぶんの解答をまとめて記録する。
 *
 * 同じ問題が2回出ることは無い想定だが、来ても順に適用して壊れないようにしてある。
 *
 * @param stats   問題ID → 履歴(この Map は変更しない)
 * @param answers こたえた順の記録
 * @param at      といた時刻(epoch ms)
 */
export function recordQuizAnswers(
  stats: ReadonlyMap<string, QuizQuestionStat>,
  answers: readonly QuizAnsweredQuestion[],
  at: number,
): Map<string, QuizQuestionStat> {
  const next = new Map(stats);
  for (const answer of answers) {
    if (!answer.questionId) continue;
    next.set(answer.questionId, recordQuizAnswer(next.get(answer.questionId), answer, at));
  }
  return next;
}

/**
 * ふくしゅうの優先度で並べる比較関数(小さいほど先に出す)。
 *
 * 1. **直近でまちがえた**問題(`streak === 0`)
 * 2. **まちがえた回数が多い**問題
 * 3. **しばらく解いていない**問題(`lastAt` が古い順)
 * 4. 問題ID(同点のときも並びが決定的になるように)
 */
export function compareReviewPriority(a: QuizQuestionStat, b: QuizQuestionStat): number {
  const recentA = a.streak === 0 ? 0 : 1;
  const recentB = b.streak === 0 ? 0 : 1;
  if (recentA !== recentB) return recentA - recentB;
  if (a.wrong !== b.wrong) return b.wrong - a.wrong;
  if (a.lastAt !== b.lastAt) return a.lastAt - b.lastAt;
  return a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0;
}

/** 苦手な問題の履歴を優先度順に返す。 */
export function weakQuestionStats(stats: Iterable<QuizQuestionStat>): QuizQuestionStat[] {
  return [...stats].filter(isWeakQuestion).sort(compareReviewPriority);
}

/** 苦手な問題のID(優先度順)。 */
export function weakQuestionIds(stats: Iterable<QuizQuestionStat>): string[] {
  return weakQuestionStats(stats).map((stat) => stat.questionId);
}

/** ふくしゅう出題の絞り込み。 */
export interface QuizReviewOptions {
  /** 出す問題数の上限(既定 `QUIZ_REVIEW.sessionQuestions`)。 */
  readonly limit?: number;
  /** この教科の問題だけに絞る(単元えらび画面の教科タブに合わせるため)。 */
  readonly subject?: QuizSubject;
}

/**
 * ふくしゅうで出す問題を選ぶ。
 *
 * **いま手元にある問題**(= 読み込んでいる帯のバンク)からしか選べない。
 * 履歴にあっても問題が見つからないもの(別の帯・配信から消えた問題)は黙って飛ばす。
 *
 * @param questions 出題候補(帯のバンクの全問)
 * @param stats     解答履歴
 */
export function selectReviewQuestions(
  questions: readonly QuizQuestion[],
  stats: Iterable<QuizQuestionStat>,
  options: QuizReviewOptions = {},
): QuizQuestion[] {
  const limit = Math.max(0, Math.trunc(options.limit ?? QUIZ_REVIEW.sessionQuestions));
  if (limit === 0) return [];
  const byId = new Map(questions.map((question) => [question.id, question]));

  const picked: QuizQuestion[] = [];
  for (const stat of weakQuestionStats(stats)) {
    const question = byId.get(stat.questionId);
    if (!question) continue;
    if (options.subject !== undefined && question.subject !== options.subject) continue;
    picked.push(question);
    if (picked.length >= limit) break;
  }
  return picked;
}

/**
 * 履歴の件数に上限を掛ける(localStorage が無限に伸びないように)。
 *
 * **苦手は捨てない**。あふれたぶんは「解けている・古い」ものから落とす。
 * 苦手だけで上限を超える異常時も、優先度の高いものから残る。
 */
export function pruneQuizStats(
  stats: Iterable<QuizQuestionStat>,
  limit: number = QUIZ_REVIEW.maxTracked,
): QuizQuestionStat[] {
  const all = [...stats];
  const max = Math.max(0, Math.trunc(limit));
  if (all.length <= max) return all;
  return all
    .sort((a, b) => {
      const weakA = isWeakQuestion(a) ? 0 : 1;
      const weakB = isWeakQuestion(b) ? 0 : 1;
      if (weakA !== weakB) return weakA - weakB;
      if (weakA === 0) return compareReviewPriority(a, b);
      // 解けている問題は**新しい順**に残す(直近性を保つ)。
      return b.lastAt - a.lastAt;
    })
    .slice(0, max);
}

// ── 集計(将来のレポート・AIチューター用。docs/07)────────────────────

/** タグ・教科などの単位でまとめた成績。 */
export interface QuizWeakGroup {
  /** まとめた単位の名前(skills タグ / 教科名)。 */
  readonly key: string;
  /** 記録のある問題数。 */
  readonly tracked: number;
  /** そのうち苦手プールに入っている問題数。 */
  readonly weak: number;
  /** 累計のせいかい数。 */
  readonly correct: number;
  /** 累計のまちがい数。 */
  readonly wrong: number;
  /** 正答率(0〜1)。1回も解いていなければ 0。 */
  readonly accuracy: number;
}

/** 苦手が多い順(同数なら正答率の低い順 → 名前順)に並べる。 */
function compareWeakGroups(a: QuizWeakGroup, b: QuizWeakGroup): number {
  if (a.weak !== b.weak) return b.weak - a.weak;
  if (a.accuracy !== b.accuracy) return a.accuracy - b.accuracy;
  return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
}

/**
 * 問題を任意の単位でまとめて集計する。
 *
 * @param keysOf 1問からまとめ先を取り出す(複数返せる = 1問が複数タグに属せる)
 */
export function aggregateQuizStats(
  questions: readonly QuizQuestion[],
  stats: Iterable<QuizQuestionStat>,
  keysOf: (question: QuizQuestion) => readonly string[],
): QuizWeakGroup[] {
  const byId = new Map(questions.map((question) => [question.id, question]));
  const buckets = new Map<
    string,
    { tracked: number; weak: number; correct: number; wrong: number }
  >();

  for (const stat of stats) {
    const question = byId.get(stat.questionId);
    if (!question) continue;
    const weak = isWeakQuestion(stat) ? 1 : 0;
    for (const key of keysOf(question)) {
      const bucket = buckets.get(key) ?? { tracked: 0, weak: 0, correct: 0, wrong: 0 };
      bucket.tracked += 1;
      bucket.weak += weak;
      bucket.correct += stat.correct;
      bucket.wrong += stat.wrong;
      buckets.set(key, bucket);
    }
  }

  return [...buckets.entries()]
    .map(([key, bucket]) => {
      const answered = bucket.correct + bucket.wrong;
      return { key, ...bucket, accuracy: answered === 0 ? 0 : bucket.correct / answered };
    })
    .sort(compareWeakGroups);
}

/** skills タグごとの成績(にがてな分野が先頭に来る)。 */
export function quizSkillStats(
  questions: readonly QuizQuestion[],
  stats: Iterable<QuizQuestionStat>,
): QuizWeakGroup[] {
  return aggregateQuizStats(questions, stats, (question) => question.skills);
}

/** 教科ごとの成績(にがてな教科が先頭に来る)。 */
export function quizSubjectStats(
  questions: readonly QuizQuestion[],
  stats: Iterable<QuizQuestionStat>,
): QuizWeakGroup[] {
  return aggregateQuizStats(questions, stats, (question) => [question.subject]);
}
